import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { Module, Source } from "@bios/types";
import { Button, Card, DataTable, Pill, type Column } from "@bios/ui";
import { useApiAuthed, useApiDev } from "../auth.js";

interface PageProps {
  devModeNoAuth?: boolean;
}

export function Sources({ devModeNoAuth }: PageProps) {
  const { module = "media" } = useParams<{ module: Module }>();
  const m = module as Module;
  const api = devModeNoAuth ? useApiDev() : useApiAuthed();
  const [sources, setSources] = useState<Source[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .sources(m)
      .then((r) => setSources(r.sources))
      .catch((e) => setError(String(e)));
  }, [api, m]);

  const trigger = async (id: string) => {
    setBusy(id);
    try {
      await api.triggerSource(id);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(null);
    }
  };

  const columns: Column<Source>[] = [
    {
      key: "name",
      header: "Source",
      render: (s) => (
        <div>
          <div style={{ fontWeight: 500 }}>{s.name}</div>
          <div style={{ fontSize: 11, color: "var(--bios-text-faint)" }}>{s.url}</div>
        </div>
      ),
    },
    { key: "type", header: "Type", render: (s) => <Pill>{s.type}</Pill> },
    {
      key: "tier",
      header: "Tier",
      render: (s) => (
        <Pill variant={s.tier === "primary" ? "signal" : s.tier === "unverified" ? "noise" : "default"}>
          {s.tier}
        </Pill>
      ),
    },
    {
      key: "weight",
      header: "Weight",
      render: (s) => (
        <span style={{ fontFamily: "var(--bios-mono)", fontSize: 12, color: weightColor(s.weight) }}>
          {(s.weight * 100).toFixed(0)}
        </span>
      ),
    },
    {
      key: "reliability",
      header: "Reliability",
      render: (s) => (
        <span style={{ fontFamily: "var(--bios-mono)", fontSize: 12 }}>
          {(s.reliability * 100).toFixed(0)}
        </span>
      ),
    },
    {
      key: "signal",
      header: "Signal %",
      render: (s) => (
        <span style={{ fontFamily: "var(--bios-mono)", fontSize: 12, color: "var(--bios-signal)" }}>
          {(s.signal_history * 100).toFixed(0)}
        </span>
      ),
    },
    {
      key: "narrative",
      header: "Narrative %",
      render: (s) => (
        <span style={{ fontFamily: "var(--bios-mono)", fontSize: 12, color: "var(--bios-noise)" }}>
          {(s.narrative_ratio * 100).toFixed(0)}
        </span>
      ),
    },
    {
      key: "last",
      header: "Last ingest",
      render: (s) => (
        <span style={{ fontSize: 12, color: "var(--bios-text-muted)" }}>
          {s.last_ingested_at ? new Date(s.last_ingested_at).toLocaleString() : "—"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (s) => (
        <Button onClick={() => trigger(s.id)} disabled={busy === s.id}>
          {busy === s.id ? "Queued…" : "Trigger"}
        </Button>
      ),
    },
  ];

  return (
    <>
      <div className="bios-page-header">
        <div>
          <h1 className="bios-page-title">Source registry</h1>
          <p className="bios-page-sub">
            Weight = 0.4·reliability + 0.4·signal share − 0.2·narrative share. Insights from low-weight sources
            get scaled down automatically.
          </p>
        </div>
      </div>
      {error && (
        <Card>
          <p style={{ color: "var(--bios-noise)", fontSize: 13 }}>{error}</p>
        </Card>
      )}
      <div className="bios-table-only">
        <Card>
          <DataTable columns={columns} rows={sources} rowKey={(s) => s.id} empty="No sources configured." />
        </Card>
      </div>
      <div className="bios-mobile-only" style={{ display: "grid", gap: 12 }}>
        {sources.length === 0 ? (
          <Card>
            <p className="bios-empty">No sources configured.</p>
          </Card>
        ) : (
          sources.map((s) => (
            <Card key={s.id}>
              <div className="bios-source-card">
                <div className="bios-source-card-head">
                  <div className="bios-source-card-name">{s.name}</div>
                  <div
                    className="bios-source-card-weight"
                    style={{ color: weightColor(s.weight) }}
                    aria-label="weight"
                  >
                    {(s.weight * 100).toFixed(0)}
                  </div>
                </div>
                <div className="bios-source-card-url">{s.url}</div>
                <div className="bios-source-card-grid">
                  <Metric label="Tier" value={s.tier} />
                  <Metric label="Reliability" value={`${(s.reliability * 100).toFixed(0)}`} />
                  <Metric
                    label="Signal %"
                    value={`${(s.signal_history * 100).toFixed(0)}`}
                    valueColor="var(--bios-signal)"
                  />
                  <Metric
                    label="Narrative %"
                    value={`${(s.narrative_ratio * 100).toFixed(0)}`}
                    valueColor="var(--bios-noise)"
                  />
                </div>
                <div className="bios-source-card-foot">
                  <span style={{ fontSize: 12, color: "var(--bios-text-muted)" }}>
                    Last ingest:{" "}
                    {s.last_ingested_at ? new Date(s.last_ingested_at).toLocaleString() : "—"}
                  </span>
                  <Button onClick={() => trigger(s.id)} disabled={busy === s.id}>
                    {busy === s.id ? "Queued…" : "Trigger"}
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </>
  );
}

function Metric({
  label,
  value,
  valueColor,
}: {
  label: string;
  value: string;
  valueColor?: string;
}) {
  return (
    <div className="bios-source-card-metric">
      <div className="bios-source-card-metric-label">{label}</div>
      <div className="bios-source-card-metric-value" style={{ color: valueColor ?? "var(--bios-text)" }}>
        {value}
      </div>
    </div>
  );
}

function weightColor(w: number): string {
  if (w >= 0.7) return "var(--bios-signal)";
  if (w >= 0.45) return "var(--bios-mixed)";
  return "var(--bios-noise)";
}
